import { cn, cva } from 'noddi-ui-common';
import { getPasswordStrength, PASSWORD_STRENGTH_SEGMENT_COUNT } from './passwordStrength';

type NoddiPasswordStrengthTranslations = {
  weak: string;
  fair: string;
  good: string;
  strong: string;
};

const defaultTexts: NoddiPasswordStrengthTranslations = {
  weak: 'Weak',
  fair: 'Fair',
  good: 'Good',
  strong: 'Strong'
};

const segmentVariants = cva('h-1 flex-1 rounded-full transition-colors', {
  variants: {
    tone: {
      empty: 'bg-gray-200',
      weak: 'bg-danger',
      fair: 'bg-warning',
      good: 'bg-blue-500',
      strong: 'bg-success'
    }
  },
  defaultVariants: {
    tone: 'empty'
  }
});

const labelVariants = cva('text-xs font-medium', {
  variants: {
    level: {
      weak: 'text-dangerDark',
      fair: 'text-warningDark',
      good: 'text-blue-600',
      strong: 'text-successDark'
    }
  }
});

type Props = {
  password: string;
  className?: string;
  translations?: NoddiPasswordStrengthTranslations;
  /** When false, hide the meter for empty passwords (default true). */
  hideWhenEmpty?: boolean;
};

export const NoddiPasswordStrength = ({ password, className, translations, hideWhenEmpty = true }: Props) => {
  const texts = translations ?? defaultTexts;
  const { level, filledSegments } = getPasswordStrength(password);

  if (hideWhenEmpty && level === 'empty') {
    return null;
  }

  if (level === 'empty') {
    return (
      <div className={cn('flex flex-col gap-1', className)}>
        <div className='flex gap-1' role='meter' aria-valuemin={0} aria-valuemax={4} aria-valuenow={0}>
          {Array.from({ length: PASSWORD_STRENGTH_SEGMENT_COUNT }, (_, index) => (
            <div key={index} className={segmentVariants({ tone: 'empty' })} />
          ))}
        </div>
      </div>
    );
  }

  const label = {
    weak: texts.weak,
    fair: texts.fair,
    good: texts.good,
    strong: texts.strong
  }[level];

  return (
    <div className={cn('flex flex-col gap-1', className)} aria-live='polite'>
      <div
        className='flex gap-1'
        role='meter'
        aria-valuemin={0}
        aria-valuemax={4}
        aria-valuenow={filledSegments}
        aria-label={label}
      >
        {Array.from({ length: PASSWORD_STRENGTH_SEGMENT_COUNT }, (_, index) => (
          <div
            key={index}
            className={segmentVariants({
              tone: index < filledSegments ? level : 'empty'
            })}
          />
        ))}
      </div>
      <span className={labelVariants({ level })}>{label}</span>
    </div>
  );
};
